import { ICurrentUser, UserInfo } from '@lingvo-news/contracts';
import { ICurrentUserStore } from './auth/current-user-store';
import { resolveCurrentUserStore } from '../store/service-container';

/**
 * Wrapper around CurrentUserStore to implement ICurrentUser abstract class
 */
export class CurrentUserWrapper extends ICurrentUser {
	constructor(private readonly userStore: ICurrentUserStore) {
		super();
	}

	async initialize(): Promise<void> {
		await this.userStore.initialize();
	}

	async refresh(): Promise<void> {
		await this.userStore.refresh();
	}

	getUserInfo(): UserInfo | null {
		const user = this.userStore.getCurrentUser();
		if (!user) {
			return null;
		}

		// Premium may be granted by different sources.
		// - Telegram Stars flow sets `premiumTier` (+ usually `premiumExpiresAt`)
		// - Buy Me a Coffee webhook sets `premiumExpiresAt` (and does NOT set `premiumTier` in session today)
		const isPremium = user.premiumExpiresAt
			? new Date(user.premiumExpiresAt).getTime() > Date.now()
			: !!user.premiumTier;

		return {
			id: user.userId,
			email: user.email,
			isAdmin: user.isAdmin,
			isPremium,
			firstName: undefined,
			lastName: undefined,
			telegramId: undefined,
			nativeLanguage: undefined,
			learnedLanguage: undefined
		};
	}

	getUserId(): string | null {
		return this.userStore.getCurrentUserId();
	}

	isAuthenticated(): boolean {
		return this.userStore.getCurrentUserId() !== null;
	}

	isAdmin(): boolean {
		const user = this.userStore.getCurrentUser();
		return user?.isAdmin === true;
	}

	isPremium(): boolean {
		const user = this.userStore.getCurrentUser();
		if (!user) return false;
		if (user.premiumExpiresAt) {
			return new Date(user.premiumExpiresAt).getTime() > Date.now();
		}
		return !!user.premiumTier;
	}

	getPremiumExpiresAt(): string | null {
		return this.userStore.getCurrentUser()?.premiumExpiresAt ?? null;
	}

	async logout(): Promise<void> {
		await this.userStore.logout();
	}
}

let currentUserWrapper: CurrentUserWrapper | null = null;

/**
 * Get the singleton CurrentUserWrapper instance
 */
export function getCurrentUserWrapper(): ICurrentUser {
	if (!currentUserWrapper) {
		currentUserWrapper = new CurrentUserWrapper(resolveCurrentUserStore());
	}
	return currentUserWrapper;
}
